import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { IconSearch } from '../components/Icons';

export default function Notifications() {
  const [target, setTarget] = useState('all'); // all, shopkeepers, deliveryboys, specific
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Live Notice Popup State
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [noticeActive, setNoticeActive] = useState(false);
  const [noticeSubmitting, setNoticeSubmitting] = useState(false);

  // Fetch users & notifications & live notice
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubHist = onSnapshot(query(collection(db, 'admin_notifications'), orderBy('sentAt', 'desc')), snap => {
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubNotice = onSnapshot(doc(db, 'settings', 'global_notice'), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setNoticeTitle(d.title || '');
        setNoticeMessage(d.message || '');
        setNoticeActive(!!d.active);
      }
    });
    return () => { unsub(); unsubHist(); unsubNotice(); };
  }, []);

  const handleSaveNotice = async (activeState) => {
    if (!noticeTitle.trim() || !noticeMessage.trim()) {
      alert('कृपया नोटीस शीर्षक आणि संदेश टाका.');
      return;
    }
    try {
      setNoticeSubmitting(true);
      await setDoc(doc(db, 'settings', 'global_notice'), {
        title: noticeTitle.trim(),
        message: noticeMessage.trim(),
        active: activeState,
        updatedAt: new Date().toISOString(),
      });
      setNoticeSubmitting(false);
      alert(activeState ? '📢 लाइव्ह नोटीस अ‍ॅपवर ऑन झाली!' : '⏸️ नोटीस बंद करण्यात आली.');
    } catch (err) {
      setNoticeSubmitting(false);
      alert('Error: ' + err.message);
    }
  };

  const sendPushNotifications = async (title, body, targetGroup, specificUserId) => {
    let targetTokens = [];
    if (targetGroup === 'all') {
      targetTokens = users.filter(u => u.expoPushToken).map(u => u.expoPushToken);
    } else if (targetGroup === 'shopkeepers') {
      targetTokens = users.filter(u => u.role === 'shopkeeper' && u.expoPushToken).map(u => u.expoPushToken);
    } else if (targetGroup === 'deliveryboys') {
      targetTokens = users.filter(u => u.role === 'deliveryboy' && u.expoPushToken).map(u => u.expoPushToken);
    } else if (targetGroup === 'specific') {
      const u = users.find(u => u.id === specificUserId);
      if (u && u.expoPushToken) targetTokens.push(u.expoPushToken);
    }

    if (targetTokens.length === 0) return;

    const messages = targetTokens.map(token => ({
      to: token,
      sound: 'default',
      title: title,
      body: body,
    }));

    try {
      await fetch('https://exp.host/--/api/v2/push/send', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Accept-encoding': 'gzip, deflate',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messages),
      });
    } catch (error) {
      console.error("Error sending push notifications", error);
    }
  };

  const sendNotification = async () => {
    if (!title || !message) return alert("कृपया शीर्षक आणि संदेश टाका");
    
    setLoading(true);
    try {
      await addDoc(collection(db, 'admin_notifications'), {
        title,
        message,
        target, // 'all', 'shopkeepers', 'deliveryboys', 'specific'
        targetUserId: target === 'specific' ? selectedUserId : null,
        targetUserName: target === 'specific' ? users.find(u => u.id === selectedUserId)?.name : 'Broadcast',
        sentAt: serverTimestamp(),
      });
      
      await sendPushNotifications(title, message, target, selectedUserId);
      
      setTitle('');
      setMessage('');
      alert("सूचना पाठवण्यात आली आहे!");
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', paddingBottom: 40 }}>
      <div className="section-title">सूचना केंद्र | Notification Center</div>
      <div className="section-subtitle">Send alerts & manage Live Announcement Popup for App users</div>

      {/* Live Notice Popup Card */}
      <div className="card" style={{ marginTop: 20, background: 'linear-gradient(135deg, rgba(255,107,53,0.06), rgba(255,107,53,0.01))', borderColor: 'var(--primary)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--primary)', margin: 0 }}>
            📢 अ‍ॅप लाइव्ह नोटीस बोर्ड (Live Announcement Popup)
          </h3>
          <span className={`badge ${noticeActive ? 'success' : 'danger'}`}>
            {noticeActive ? '🟢 Live on App' : '🔴 Disabled'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>नोटीस शीर्षक (Popup Title)</label>
            <input
              type="text"
              placeholder="उदा. 🌧️ हवामान अपडेट / विशेष ऑफर!"
              value={noticeTitle}
              onChange={e => setNoticeTitle(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
            />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>नोटीस संदेश (Popup Message)</label>
            <input
              type="text"
              placeholder="उदा. आज पावसामुळे डिलिव्हरीला वेळ लागू शकतो."
              value={noticeMessage}
              onChange={e => setNoticeMessage(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={noticeSubmitting}
            onClick={() => handleSaveNotice(false)}
          >
            ⏸️ नोटीस बंद करा (Turn Off)
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={noticeSubmitting}
            onClick={() => handleSaveNotice(true)}
          >
            🚀 अ‍ॅपवर ऑन करा (Publish & Turn On)
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 24, marginTop: 24 }}>
        {/* Create Form */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📣 नवीन सूचना (New Notification)</div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>कोणाला पाठवायचे? (Target Group)</label>
              <select 
                className="input" 
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                value={target}
                onChange={e => setTarget(e.target.value)}
              >
                <option value="all">सर्व युजर्स (All Users)</option>
                <option value="shopkeepers">सर्व दुकानदार (All Shopkeepers)</option>
                <option value="deliveryboys">सर्व डिलिव्हरी बॉईज (All Delivery Boys)</option>
                <option value="specific">एका विशिष्ट व्यक्तीला (Specific User)</option>
              </select>
            </div>

            {target === 'specific' && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>युजर निवडा (Select User)</label>
                <select 
                  className="input" 
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                >
                  <option value="">युजर निवडा...</option>
                  {users.sort((a,b) => a.name?.localeCompare(b.name)).map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role}) - {u.phone}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>शीर्षक (Title)</label>
              <input 
                placeholder="उदा. उद्या सुट्टी आहे"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </div>

            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>संदेश (Message)</label>
              <textarea 
                placeholder="तुमचा संदेश इथे लिहा..."
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', minHeight: 100, fontFamily: 'inherit' }}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>

            <button 
              className="btn btn-primary" 
              style={{ padding: 14, justifyContent: 'center', fontSize: 15 }}
              onClick={sendNotification}
              disabled={loading}
            >
              {loading ? 'पाठवत आहे...' : '🔔 सूचना पाठवा (Send Now)'}
            </button>
          </div>
        </div>

        {/* History List */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📜 मागील सूचना (History)</div>
          </div>
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            {history.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No history found.</div>
            ) : history.map(h => (
              <div key={h.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{h.title}</span>
                  <span className="badge muted" style={{ fontSize: 10 }}>{h.target}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>{h.message}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>To: {h.targetUserId ? h.targetUserName : h.target}</span>
                  <span>{h.sentAt?.toDate().toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
